import React from 'react';
import { useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useSearchParams } from 'react-router-dom';
import privateRoutes from './PrivateRoutes';
import PublicRoutes from './PublicRoutes';
import { publicPaths } from './paths/publicPaths';
import { getFirstAccessibleRoute } from './utils/get-first-accessible-route';
import { useAuthStoreHydrated } from '@/core/hooks/useAuthStoreHydrated';
import { useError } from '@/core/hooks/useError';
import { useHttpClient } from '@/core/hooks/useHttpClient';
import { useAuthStore } from '@/core/stores/authStore';

const EMPTY_PERMISSIONS: string[] = [];

const ProtectedRoute: React.FC<{ children: React.ReactNode; permissions?: string[] }> = ({
  children,
  permissions = EMPTY_PERMISSIONS,
}) => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const hasAnyPermission = useAuthStore((state) => state.hasAnyPermission);
  const hasHydrated = useAuthStoreHydrated();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  if (!hasHydrated) return null;
  if (!isAuthenticated) {
    const loginPath = token ? `${publicPaths.LOGIN}?token=${token}` : publicPaths.LOGIN;
    return <Navigate to={loginPath} replace />;
  }
  if (permissions.length > 0 && !hasAnyPermission(permissions)) {
    return <Navigate to={publicPaths.UNAUTHORIZED} replace />;
  }

  return <>{children}</>;
};

const DefaultRoute: React.FC = () => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const hasAnyPermission = useAuthStore((state) => state.hasAnyPermission);
  const hasHydrated = useAuthStoreHydrated();
  const [searchParams] = useSearchParams();

  if (!hasHydrated) return null;
  if (isAuthenticated) {
    return <Navigate to={getFirstAccessibleRoute(hasAnyPermission)} replace />;
  }

  const loginPath = searchParams.get('token')
    ? `${publicPaths.LOGIN}?token=${searchParams.get('token')}`
    : publicPaths.LOGIN;

  return <Navigate to={loginPath} replace />;
};

const AppContent: React.FC = () => {
  return (
    <Routes>
      {PublicRoutes.map((route) => (
        <Route key={route.path} path={route.path} element={route.component} />
      ))}

      {privateRoutes.map(({ path, component, layout: Layout = React.Fragment, permissions }) => (
        <Route
          key={path}
          path={path}
          element={
            <ProtectedRoute permissions={permissions}>
              <Layout>{component}</Layout>
            </ProtectedRoute>
          }
        />
      ))}

      <Route path="*" element={<DefaultRoute />} />
    </Routes>
  );
};

export const AppRouter: React.FC = () => {
  const { showError } = useError();
  const setErrorHandler = useHttpClient().setErrorHandler;

  useEffect(() => {
    setErrorHandler(showError);

    return () => {
      setErrorHandler(() => {});
    };
    // react-doctor-disable-next-line react-doctor/exhaustive-deps -- stable refs/functions; including them would not change behavior
  }, [showError]);

  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
};
